import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import validationMessage from 'webapp/helpers/validation-message';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import FieldHelpboxComponent from 'webapp/components/field-helpbox';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormInputComponent from 'webapp/components/form/input';
import FormInputA1Component from 'webapp/components/form/input-a1';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import type { OptionRaw, Value as SelectValue } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import FormSelectFormatComponent from 'webapp/components/form/select-format';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import FormTextareaComponent from 'webapp/components/form/textarea';
import MetricEditInvertComponent from 'webapp/components/metric/edit/invert';
import QueryBuilderFieldCustomCodeComponent from 'webapp/components/query-builder/field/custom-code';
import QueryBuilderFieldMetricDefinitionComponent from 'webapp/components/query-builder/field/metric-definition';
import RowErrorComponent from 'webapp/components/row-error';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { NameIdPair } from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';

interface Args {
	form: CustomMetricSetupFormPrebuilt;
	model: CustomMetricSetupFormItemField;
	isDisabled: boolean;
	onContentChange: () => void;
	groupItems?: CustomMetricSetupFormItemField[];
}

export default class CustomQuerySetupPrebuiltFormFieldComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;

	dateFormat = 'YYYY-MM-DD';
	timeFomat = 'HH:mm';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.initQueryParams();

		if (this.args.model.depends) {
			scheduleOnce('afterRender', this, this.checkIfNeedToHide);
		}

		const optionValues = this.args.model.getOptionValues() as LabelValuePairString[];
		const orderedOptionValues = this.orderOptionValues(optionValues);

		// Merge orphaned saved values into the option list at init: `{{did-update}}` doesn't
		// fire on the first render, and mutating tracked optionValues post-read is rejected
		// by Glimmer ("modified after read").
		if (this.args.model.isCombo || this.args.model.isSelect) {
			const value = this.args.model.value;
			const savedValues =
				typeof value === 'string'
					? [value]
					: Array.isArray(value)
						? (value as string[])
						: [];
			savedValues.forEach((val) => {
				if (val && !orderedOptionValues.find((opt) => opt.value === val)) {
					orderedOptionValues.push({ label: val, value: val });
				}
			});
		}

		this.optionValues = orderedOptionValues;
	}

	get formAsSheet() {
		return this.args.form as CustomMetricSetupFormSheet;
	}

	get source() {
		return this.args.form.query.sourceObj!;
	}

	get valueAsString() {
		return this.args.model.value as string;
	}

	set valueAsString(value: string) {
		// for a1
		this.args.model.value = value;
	}

	get valueAsBoolean() {
		return this.args.model.value as boolean;
	}

	get valueAsMetricDefinition() {
		return this.args.model.value as APIData.CustomQuery.MetricDefinitionValue;
	}

	get valueAsStringOrNumber() {
		return this.args.model.value as string | number;
	}

	get valueAsSelectValue() {
		return this.args.model.value as SelectValue;
	}

	get optionValuesForFormat() {
		return this.optionValues as OptionRaw<string>[];
	}

	get groupItems(): CustomMetricSetupFormItemField[] {
		return this.args.groupItems || [];
	}

	get isHelpboxCurrentlyOpen(): boolean {
		return !!this.args.model.helpbox && this.args.model.isHelpboxOpen;
	}

	get showReadMore(): boolean {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	get showValidationErrorTooltip(): boolean {
		return (
			!!this.args.model.validationMethod &&
			!!this.args.model.errorsArray?.length &&
			!!this.args.model.errors.value
		);
	}

	get codeEditorOptions(): SomeDataObject {
		return {
			mode: this.args.model.isSQL ? 'sql' : 'json',
		};
	}

	// --------- dropdown / field options & their fetching ----------

	@tracked optionValues: LabelValuePairNumeric[] | LabelValuePairString[] = [];
	@tracked queryInProgress = false;
	@tracked isQueried = false;
	@tracked queryParams: SomeDataObject | null = null;

	get maxSelectionLength(): number {
		return this.args.model.maxItems || Infinity;
	}

	get extraClassForScale(): string {
		return this.args.model.isScale ? 'dropdown-format' : '';
	}

	get actionOptions(): ActionOption[] {
		const actions: ActionOption[] = [];
		if (this.args.model.method) {
			actions.push({
				id: null,
				text: 'Refresh list',
				isSpecial: true, // don't count in tabbed dropdowns
				onClick: (selectElement: JQuery<HTMLElement>) => {
					void this.queryOptionValues(undefined, true).then(() => {
						next(() => {
							selectElement.select2('open');
						});
					});
				},
				html: '<a role="button" class="button-refresh"><ui-icon name="Refresh" size="sm"></ui-icon><span>Refresh list</span></a>',
				position: 'bottom',
			});
		}
		return actions;
	}

	get selectedOptions() {
		const value = this.args.model.value as number | string | (number | string)[];
		if (typeof value === 'string' || typeof value === 'number') {
			const option = this.optionValues.find((o) => o.value === value);
			if (option) {
				return [option];
			}
		} else if (Array.isArray(value)) {
			// multiselect
			return this.optionValues.filter((o) => value.includes(o.value));
		}
		return [];
	}

	private orderOptionValues(optionValues: LabelValuePairString[]): LabelValuePairString[] {
		return optionValues.sort((a, b) => a.label.localeCompare(b.label));
	}

	private queryOptionValues(cb?: () => void, forceRefresh = false): Promise<void> {
		if (this.isQueried && !forceRefresh) {
			return Promise.resolve();
		}

		const data: SomeDataObject = {
			method: this.args.model.method,
		};

		if (this.queryParams) {
			data.parameters = this.queryParams;
		}

		if (forceRefresh) {
			data.forceRefresh = true;
		}

		this.queryInProgress = true;
		this.isQueried = true;

		return this.requestService
			.api<APIData.CustomQuery.Prebuilt.BuilderOptionsResponse>(
				'POST',
				this.args.form.query.builderUrl,
				data,
				{
					timeout: 5 * 60 * 1000,
				},
			)
			.then(
				(reply) => {
					const optionValues: OptionRaw<string>[] = [];
					(reply.items || []).forEach((item) => {
						optionValues.push({
							value: item.id,
							label: item.name,
							disabled: item.disabled,
							hidden: item.hidden,
						});
					});
					// if a combo, must add already entered values to the list
					if (this.args.model.valueFormatted) {
						if (this.args.model.isMultiCombo) {
							(this.args.model.valueFormatted as NameIdPair[]).forEach((item) => {
								if (!optionValues.find((option) => option.value === item.id)) {
									optionValues.push({
										value: item.id,
										label: item.name,
									});
								}
							});
						} else if (this.args.model.isCombo) {
							const valueFormatted = this.args.model.valueFormatted as NameIdPair;
							if (!optionValues.find((item) => item.value === valueFormatted.id)) {
								optionValues.push({
									value: valueFormatted.id,
									label: valueFormatted.name,
								});
							}
						} else {
							// reset currently selected values if not in the list
							if (this.args.model.isMultiSelect) {
								// keep only the still-valid selections, dropping any
								// missing/disabled/hidden options instead of clearing all
								const selected = this.args.model.valueFormatted as NameIdPair[];
								const validSelected = selected.filter((item) => {
									const option = optionValues.find((o) => o.value === item.id);
									return option && !option.disabled && !option.hidden;
								});
								if (validSelected.length !== selected.length) {
									this.setValue(validSelected.map((item) => item.id));
								}
							} else if (this.args.model.isSingleSelect) {
								const valueFormatted = this.args.model.valueFormatted as NameIdPair;
								const option = optionValues.find(
									(item) => item.value === valueFormatted.id,
								);
								if (!option || option.disabled || option.hidden) {
									this.setValue('');
								}
							}
						}
					}
					if (isAlive(this)) {
						this.optionValues = this.orderOptionValues(optionValues);
					}
					if (cb) {
						scheduleOnce('afterRender', this, cb);
					}
				},
				(errorData) => {
					if (isAlive(this)) {
						this.isQueried = false;
					}

					throw errorData;
				},
			)
			.finally(() => {
				this.queryInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	private getOtherField(fieldId: string): CustomMetricSetupFormItemField | null {
		let item = this.args.form.items.find(
			(item) => item.id === fieldId,
		) as CustomMetricSetupFormItemField;
		if (!item && this.groupItems.length > 0) {
			item = this.groupItems.find((item) => item.id === fieldId)!;
		}
		return item || null;
	}

	private getOtherFieldValue(fieldId: string | string[]) {
		let value;
		// parameters can be also arrays
		if (typeof fieldId === 'string') {
			// groupitems first, if exist
			const item = this.getOtherField(fieldId);
			value = item ? item.value : null;
		} else {
			value = [];
			fieldId.forEach((fId) => {
				const v = this.getOtherFieldValue(fId);
				if (v) {
					value.push(v);
				}
			});
		}
		return value;
	}

	private initQueryParams(): void {
		const parameters = this.args.model.parameters;
		if (!parameters) {
			return;
		}
		const paramValues: SomeDataObject = {};
		Object.entries(parameters).forEach(([paramKey, paramId]) => {
			const value = this.getOtherFieldValue(paramId);
			if (value) {
				paramValues[paramKey] = value;
			}
		});
		this.queryParams = paramValues;
	}

	private checkParameterValues(): boolean {
		const parameters = this.args.model.parameters;

		if (!parameters || !isAlive(this)) {
			return false;
		}

		const paramValues: SomeDataObject = {};

		let isDifferent = false,
			isMissing = false;

		Object.entries(parameters).forEach(([paramKey, paramId]) => {
			const value = this.getOtherFieldValue(paramId) || '',
				previousValue = this.queryParams?.[paramKey] || '';

			paramValues[paramKey] = value;
			if (String(previousValue) !== String(value as string)) {
				isDifferent = true;
			}
			if (isEmpty(value)) {
				isMissing = true;
			}
		});

		if (isDifferent) {
			this.isQueried = false;
			this.queryParams = paramValues;

			// don't reset the value, keep other fields selected, but must be requeried
			const selectedOptions = this.selectedOptions;
			if (selectedOptions.length) {
				// to keep the selected value in the dropdown
				this.optionValues = selectedOptions as
					LabelValuePairString[] | LabelValuePairNumeric[];
			} else {
				this.optionValues = [];
			}
		}

		return !isMissing;
	}

	// --------- general functionality --------

	private checkIfNeedToHide(): void {
		let doHide = false;

		const dependsField = this.getOtherField(this.args.model.depends);

		if (dependsField?.isHidden) {
			if (!this.args.model.isHidden) {
				this.args.model.isHidden = true;
			}
			return;
		}

		const value = this.getOtherFieldValue(this.args.model.depends);

		if (this.args.model.dependsShow || this.args.model.dependsHide) {
			// empty depends when dependsShow/Hide is set means it should be hidden
			// https://app.asana.com/0/7582091858306/1199540247532583/f
			if (isEmpty(value)) {
				doHide = true;
			} else {
				// transform to array for easier compare
				const valueArray: string[] = Array.isArray(value) ? value : [value as string];

				// dependsShow: if chosen value is or contains at least one of given values, field will show
				if (this.args.model.dependsShow.length) {
					const conditionValues = this.args.model.dependsShow;

					doHide = true;
					conditionValues.forEach((val) => {
						if (valueArray.includes(val)) {
							doHide = false;
						}
					});
				}

				// dependsHide: if chosen value is a subset of given values (and nothing else), field will hide
				if (this.args.model.dependsHide.length) {
					const conditionValues = this.args.model.dependsHide;

					// other values must not be present
					doHide = true;
					valueArray.forEach((val) => {
						if (!conditionValues.includes(val)) {
							doHide = false;
						}
					});
				}
			}
		} else {
			doHide = isEmpty(value);
		}

		if (this.args.model.isHidden !== doHide) {
			this.args.model.isHidden = doHide;
		}
	}

	@action
	onSomeValueChange(): void {
		if (this.args.model.method && this.args.model.parameters) {
			// need to compare if parameters have changed
			once(this, this.checkParameterValues);
		}
		if (this.args.model.depends) {
			once(this, this.checkIfNeedToHide);

			// // reset dependet input values if method is set and parameters have changed
			// if (
			// 	this.args.model.method &&
			// 	(!this.args.model.parameters || this.checkParameterValues())
			// ) {
			// 	once(this, this.queryOptionValues);
			// }
		}
	}

	@action
	onThisValueChange(): void {
		once(this.args.model, this.args.model.updateValueFormatted, this.optionValues);
	}

	@action
	checkValueInOptionValues(): void {
		// Selects: keep an orphaned saved value visible. Combos: existing externalFieldUpdate path.
		if (!this.args.model.isCombo && !this.args.model.isSelect) {
			return;
		}

		let vals: string[] = [];

		const value = this.args.model.value as string | string[];
		if (typeof value === 'string') {
			vals = [value];
		} else {
			vals = value || [];
		}

		vals.forEach((val) => {
			if (
				val &&
				!(this.optionValues as LabelValuePairString[]).find((item) => item.value === val)
			) {
				this.addCustomValue(val);
			}
		});
	}

	@action
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setValue(value: any) {
		this.args.model.value = value;
		this.args.model.updateValueFormatted();
		next(() => {
			this.triggerItemAction('change');
		});
	}

	@action
	triggerItemAction(actionName: string): void {
		this.args.form.handleItemAction(this.args.model, actionName);
	}

	@action
	triggerFocusIn(): void {
		this.triggerItemAction('focus-in');
	}

	@action
	dropdownOpened(cb: () => void): boolean {
		if (this.args.model.method) {
			if (!this.args.model.parameters || this.checkParameterValues()) {
				// if no method - query once and be done with it
				// if have method - have to check if parameters have changed before query
				void this.queryOptionValues(cb);
			}
		}

		return !!this.args.model.parameters;
	}

	@action
	addCustomValue(value: string): boolean {
		// only scale has numeric pair, and custom values cannot be added there
		this.optionValues = [
			...(this.optionValues as LabelValuePairString[]),
			{
				label: value,
				value,
			},
		];
		return true;
	}

	@action
	addCustomFormatValue(value: string): boolean {
		this.setValue(value);
		return this.addCustomValue(value);
	}

	@action
	toggleHelpbox(isOpen: boolean) {
		this.args.model.isHelpboxOpen = isOpen;
	}
	<template>
		<div
			class="{{if @model.description 'infoTooltipped'}}
				{{if this.isHelpboxCurrentlyOpen 'helpboxOpen'}}"
			{{didInsert @onContentChange}}
			{{didUpdate this.onThisValueChange @model.value}}
			{{didUpdate this.onSomeValueChange @form.valueChangeCounter @form.hiddenChangeCounter}}
			{{didUpdate this.checkValueInOptionValues @model.value}}
			{{willDestroy @onContentChange}}
		>

			{{#if @model.isSelect}}
				<FormSelectWithActionComponent
					name={{@model.id}}
					{{! @glint-ignore: select typings all over the place }}
					@selected={{this.valueAsSelectValue}}
					@options={{this.optionValues}}
					@actionOptions={{this.actionOptions}}
					@isNumeric={{@model.isValueNumeric}}
					@placeholder={{@model.name}}
					@onOpen={{this.dropdownOpened}}
					@isLoading={{this.queryInProgress}}
					@isMultiple={{@model.isMultiSelect}}
					@isRequired={{@model.required}}
					@maxSelectionLength={{this.maxSelectionLength}}
					@isDisabled={{@isDisabled}}
					@onFocus={{this.triggerFocusIn}}
					@isDropdownWider={{@model.isScale}}
					@extraClass={{this.extraClassForScale}}
					@onChange={{this.setValue}}
					@hideLabel3OnSelection={{true}}
					@theme="dark"
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo=".select2"
						@spaceRight={{@model.isSingleValue}}
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isCombo}}
				{{#if @model.isFormat}}
					<FormSelectFormatComponent
						name={{@model.id}}
						@selected={{this.valueAsString}}
						@options={{this.optionValuesForFormat}}
						@placeholder={{@model.name}}
						@onChange={{this.setValue}}
						@onAdd={{this.addCustomFormatValue}}
						@isDisabled={{@isDisabled}}
						@theme="dark"
					/>
				{{else}}
					<FormSelectComponent
						name={{@model.id}}
						{{! @glint-ignore: select typings all over the place }}
						@selected={{this.valueAsSelectValue}}
						@options={{this.optionValues}}
						@onChange={{this.setValue}}
						@isNumeric={{@model.isValueNumeric}}
						@placeholder={{@model.name}}
						@onOpen={{this.dropdownOpened}}
						@isLoading={{this.queryInProgress}}
						@isMultiple={{@model.isMultiCombo}}
						@maxSelectionLength={{this.maxSelectionLength}}
						@onCustomAdd={{this.addCustomValue}}
						@isDisabled={{@isDisabled}}
						@theme="dark"
					/>
					{{#if @model.value}}
						<ToolTipComponent
							@text={{this.valueAsString}}
							@position="top"
							@theme="dark"
						/>
					{{/if}}
				{{/if}}
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo=".select2"
						@spaceRight={{@model.isSingleValue}}
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isTextarea}}
				<FormTextareaComponent
					name={{@model.id}}
					@value={{@model.value}}
					placeholder={{@model.name}}
					disabled={{@isDisabled}}
					@focusIn={{this.triggerFocusIn}}
					@theme="dark"
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo="textarea"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isCodeEditor}}
				<QueryBuilderFieldCustomCodeComponent
					@text={{this.valueAsString}}
					@placeholder={{@model.name}}
					@options={{this.codeEditorOptions}}
					@disabled={{@isDisabled}}
					@onTextChange={{this.setValue}}
				/>
				{{#if @model.helpbox}}
					<FieldHelpboxComponent
						@text={{@model.helpbox}}
						@isOpen={{@model.isHelpboxOpen}}
						@onToggleHelpbox={{this.toggleHelpbox}}
					/>
				{{else if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo="textarea"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isDatepicker}}
				<FormDatetimePickerComponent
					@dateFormat={{this.dateFormat}}
					@timeFormat={{this.timeFomat}}
					@value={{this.valueAsString}}
					@disableTimePicker={{@model.isDate}}
					@disableDatePicker={{@model.isTime}}
					@placeholder={{@model.name}}
					@isDisabled={{@isDisabled}}
					@theme="dark"
					@onChange={{this.setValue}}
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo="input"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isCheckbox}}
				<FormCheckboxComponent
					@name={{@model.id}}
					@selected={{this.valueAsBoolean}}
					@label={{@model.name}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.setValue}}
					@theme="dark"
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo=".checkbox"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isRadio}}
				<FormRadiobuttonGroupComponent
					class="radiobutton-default"
					@name={{@model.id}}
					@selected={{this.valueAsStringOrNumber}}
					@options={{this.optionValues}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.setValue}}
					@theme="dark"
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo=".radiobutton-default"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isMetricDefinition}}
				<FormInputComponent
					name={{@model.id}}
					@type="hidden"
					@value={{@model.value}}
					@theme="dark"
					disabled={{true}}
				/>
				<QueryBuilderFieldMetricDefinitionComponent
					@model={{this.valueAsMetricDefinition}}
					@builderType={{@form.builderType}}
					@source={{this.source}}
					@isDisabled={{@isDisabled}}
					@onSave={{this.setValue}}
					@openMetricDefinitionPopupOnInit={{@form.openMetricDefinitionPopupOnInit}}
				/>
			{{else if @model.isMetricInvert}}
				<MetricEditInvertComponent
					@value={{this.valueAsBoolean}}
					@isDisabled={{@isDisabled}}
					@showReadMore={{this.showReadMore}}
					@theme="dark"
					@onChange={{this.setValue}}
				/>
			{{else if @model.isA1}}
				<FormInputA1Component
					@form={{this.formAsSheet}}
					@name={{@model.id}}
					@value={{this.valueAsString}}
					@placeholder={{@model.name}}
					@isFocused={{@model.isFocused}}
					@hasValue={{@model.hasValue}}
					@isDisabled={{@isDisabled}}
					@onItemAction={{this.triggerItemAction}}
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo=".input-a1"
						@theme="dark"
					/>
				{{/if}}
			{{else if @model.isNumber}}
				<FormInputComponent
					name={{@model.id}}
					@type="number"
					@value={{@model.value}}
					placeholder={{@model.name}}
					min={{@model.minValue}}
					max={{@model.maxValue}}
					disabled={{@isDisabled}}
					@focusIn={{this.triggerFocusIn}}
					@theme="dark"
				/>
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo="input"
						@theme="dark"
					/>
				{{/if}}
			{{else}}
				<FormInputComponent
					name={{@model.id}}
					@value={{@model.value}}
					placeholder={{@model.name}}
					disabled={{@isDisabled}}
					@focusIn={{this.triggerFocusIn}}
					@theme="dark"
				/>
				{{#if @model.value}}
					<ToolTipComponent @text={{this.valueAsString}} @position="top" @theme="dark" />
				{{/if}}
				{{#if @model.description}}
					<ToolTipWithIconComponent
						@text={{@model.description}}
						@appendTo="input"
						@theme="dark"
					/>
				{{/if}}
			{{/if}}

			<RowErrorComponent @items={{@model.errorsArray}} @skipFormError={{true}} />

			{{#if this.showValidationErrorTooltip}}
				<ToolTipWithIconComponent
					@text={{validationMessage @model.errors.value}}
					@iconName="AlertFill"
					@maxWidth={{300}}
					@theme="red"
				/>
			{{else if @model.validationWarning}}
				<ToolTipWithIconComponent
					@text={{@model.validationWarning}}
					@iconName="AlertFill"
					@maxWidth={{300}}
					@theme="orange"
				/>
			{{/if}}

		</div>
	</template>
}
